/**
 * The rate figures each SOA's "Asset assumptions" appendix prints — one implementation, because
 * both the aged care and Support at Home booklets show the same table and had their own copies of
 * the defaults to drift apart.
 *
 * The rule this exists to enforce: THE APPENDIX MUST STATE WHAT THE MODEL ACTUALLY USED. That is
 * not always the asset's stored rate field:
 *   - INVESTMENT PROPERTY is the case that made this necessary. The engine reads a property's
 *     RENT and EXPENSES in dollars (incomeAssessment.ts) and never consults a yield percentage, so
 *     a stored `incomeRate` is dead data — the appendix used to print the firm's assumed yield
 *     beside a property whose modelled rent implied something else entirely. The row now reports
 *     the yield the recorded rent actually represents, and names the dollars behind it.
 *   - SUPER AND PENSION balances flow nothing to the client personally (an ABP's cash is its
 *     pension payment, a separate line), so their income figure is 0 by design.
 * Everything else reports its own stored rate against the published default for its class.
 */
import { formatCurrency } from './support/currency.ts';
import { ASSET_CLASS_ASSUMPTIONS, INVESTMENT_PROFILES } from './investmentProfiles.ts';

export interface AssetAssumptionFigures {
    /** The income rate the MODEL used, as a percentage (for property, the recorded rent's yield). */
    incomeRate: number;
    growthRate: number;
    /** True where the figures are this client's own rather than the published assumption. */
    overridden: boolean;
    /** Plain-language note on where the figures came from — '' where the rate speaks for itself. */
    basis: string;
}

const num = (v: unknown): number => (typeof v === 'number' ? v : parseFloat(String(v ?? '')) || 0);

/** The published default rates for an asset class, read at use time (a firm's own where set). */
function defaultsFor(assetType: string): { incomeRate: number; growthRate: number } {
    const ac = ASSET_CLASS_ASSUMPTIONS;
    switch (assetType) {
        case 'bankAccounts': return { incomeRate: ac.bankIncomePct, growthRate: 0 };
        case 'termDeposits': return { incomeRate: ac.termDepositIncomePct, growthRate: 0 };
        case 'shares': return { incomeRate: ac.sharesIncomePct, growthRate: ac.sharesGrowthPct };
        case 'investmentProperty': return { incomeRate: ac.propertyYieldPct, growthRate: ac.propertyGrowthPct };
        case 'superannuation': return { incomeRate: 0, growthRate: INVESTMENT_PROFILES.balanced.accumulationNetPct };
        case 'accountBasedPensions': return { incomeRate: 0, growthRate: INVESTMENT_PROFILES.balanced.pensionNetPct };
        default: return { incomeRate: 0, growthRate: 0 };
    }
}

export function assetAssumptionFiguresFor(assetType: string, asset: Record<string, unknown>): AssetAssumptionFigures {
    const defaults = defaultsFor(assetType);
    const growthRate = num(asset.growthRate);

    if (assetType === 'investmentProperty') {
        // What the projection actually charges and credits: the recorded dollars.
        const value = num(asset.value);
        const rent = num(asset.rent);
        const expenses = num(asset.expenses);
        const impliedYield = value > 0 ? (rent / value) * 100 : 0;
        const basis = rent > 0 || expenses > 0
            ? `Rent ${formatCurrency(Math.round(rent))} p.a., expenses ${formatCurrency(Math.round(expenses))} p.a. — as recorded, not an assumed yield`
            : 'No rent recorded, so the model treats this property as earning nothing';
        return { incomeRate: impliedYield, growthRate, overridden: true, basis };
    }

    const incomeRate = num(asset.incomeRate);
    return {
        incomeRate,
        growthRate,
        overridden: incomeRate !== defaults.incomeRate || growthRate !== defaults.growthRate,
        basis: '',
    };
}
